import { ref, reactive, Ref, type ComputedRef } from 'vue';
import { isIncludeTextWithoutWhitespace } from '@/common/utils/commonUtils';
import { FILTER_SEARCH_OPERATORS } from '@/common/components/molecules/filterSearch/filterSearch.define';
import {
  EditingFilterToken,
  FilterDropdownInfo,
  FilterDropdownItem,
  FilterItem,
  FilterSearchOperator,
  FilterToken,
} from '@/common/components/molecules/filterSearch/filterSearch.type';

const MAX_DROPDOWN_DISPLAY_COUNT = 50;

interface UseFilterDropdownParams {
  filterItems: Ref<FilterItem[]>;
  editingToken: Ref<EditingFilterToken | null>;
  lastToken: ComputedRef<FilterToken>;
  autoValueItems?: Ref<Record<string, { id: string; name: string }[]> | undefined>;
}

export function useFilterDropdown({
  filterItems,
  editingToken,
  lastToken,
  autoValueItems,
}: UseFilterDropdownParams) {
  const isShowDropdown = ref(false);
  const dropdownSearchText = ref('');
  const dropdownListInfo = reactive<FilterDropdownInfo>({
    multi: false,
    category: 'key',
    items: [],
    filteredItems: [],
    isEditing: false,
    isAutoValueItems: false,
  });

  const dropdownListRef = ref();
  const filterMainRef = ref();
  const hoveredDropdownItemIndex = ref<number>(-1);
  const isKeyboardNavigating = ref<boolean>(false);

  const initHoveredDropdownItemIndex = () => {
    hoveredDropdownItemIndex.value = -1;
    isKeyboardNavigating.value = false;
  };

  const showDropdownList = () => {
    isShowDropdown.value = true;
  };

  const hideDropdownList = () => {
    isShowDropdown.value = false;
    dropdownSearchText.value = '';
    initHoveredDropdownItemIndex();
  };

  const isSelectedDropdownItem = ({ id }: FilterDropdownItem): boolean => {
    const { category } = dropdownListInfo;

    const targetToken: FilterToken = editingToken.value ?? lastToken.value;
    if (targetToken) {
      switch (category) {
        case 'operator':
          return targetToken.operator?.id === id;

        case 'value':
          return targetToken.values.findIndex((value) => value.id === id) > -1;

        case 'key':
        default:
          break;
      }
    }

    return false;
  };

  const setKeyDropdownList = () => {
    dropdownListInfo.category = 'key';
    dropdownListInfo.multi = false;
    dropdownListInfo.isAutoValueItems = false;
    dropdownSearchText.value = '';
    dropdownListInfo.items =
      filterItems.value.map(({ key }) => {
        const isDefaultColumn = filterItems.value.find(
          (item) => item.key.id === key.id,
        )?.defaultSearchField;
        return {
          id: key.id,
          name: key.name,
          isDefaultColumn,
        };
      }) ?? [];

    const defaultColumns = dropdownListInfo.items.filter((item) => item.isDefaultColumn);
    const normalColumns = dropdownListInfo.items.filter((item) => !item.isDefaultColumn);
    dropdownListInfo.items = [...defaultColumns, ...normalColumns];

    dropdownListInfo.filteredItems = dropdownListInfo.items.map((item) => ({
      ...item,
      checked: false,
    }));
    initHoveredDropdownItemIndex();
  };

  const setOperatorDropdownList = (keyId: string) => {
    dropdownListInfo.category = 'operator';
    dropdownListInfo.multi = false;
    dropdownListInfo.isAutoValueItems = false;
    dropdownSearchText.value = '';

    dropdownListInfo.items =
      filterItems.value
        .find((item) => item.key.id === keyId)
        ?.operators?.filter((operator) =>
          FILTER_SEARCH_OPERATORS.includes(operator as FilterSearchOperator),
        )
        ?.map((operator: string) => ({
          id: operator,
          name: operator,
        })) ?? [];

    dropdownListInfo.filteredItems = dropdownListInfo.items.map((item) => ({
      ...item,
      checked: isSelectedDropdownItem(item),
    }));

    initHoveredDropdownItemIndex();
  };

  const setValueDropdownList = (
    keyId: string,
    filterFn?: (item: FilterDropdownItem) => boolean,
    sortFn?: (a: FilterDropdownItem, b: FilterDropdownItem) => number,
  ) => {
    dropdownListInfo.category = 'value';
    dropdownListInfo.multi = !!filterItems.value.find(({ key }) => key.id === keyId)?.values?.multi;
    dropdownSearchText.value = '';

    const predefinedItems =
      filterItems.value
        .find((item) => item.key.id === keyId)
        ?.values?.items?.map(({ id, name }) => ({ id, name })) ?? [];

    if (predefinedItems.length) {
      dropdownListInfo.items = predefinedItems;
      dropdownListInfo.isAutoValueItems = false;
    } else {
      const autoItems = autoValueItems?.value?.[keyId] ?? [];
      dropdownListInfo.items = autoItems;
      dropdownListInfo.isAutoValueItems = autoItems.length > 0;
    }

    if (filterFn && !dropdownListInfo.isAutoValueItems) {
      dropdownListInfo.items = dropdownListInfo.items.filter(filterFn);
    }

    const mapped = dropdownListInfo.items.map((item) => ({
      ...item,
      checked: isSelectedDropdownItem(item),
    }));

    if (sortFn) {
      mapped.sort(sortFn);
    }

    dropdownListInfo.filteredItems =
      dropdownListInfo.isAutoValueItems && mapped.length > MAX_DROPDOWN_DISPLAY_COUNT
        ? mapped.slice(0, MAX_DROPDOWN_DISPLAY_COUNT)
        : mapped;
  };

  const setMultiSelectedValues = (
    targetToken: FilterToken,
    selectedDropdownItem: FilterDropdownItem,
  ) => {
    const index = targetToken.values.findIndex(({ id }) => id === selectedDropdownItem.id);
    if (index !== -1) {
      targetToken.values.splice(index, 1);
    } else {
      targetToken.values.push(selectedDropdownItem);
    }

    dropdownListInfo.filteredItems.forEach((listItem) => {
      listItem.checked = isSelectedDropdownItem(listItem);
    });
  };

  const filterByInput = (searchWord: string) => {
    dropdownSearchText.value = searchWord;
    const normalizedSearch = searchWord.toUpperCase().replace(/\s+/g, '');

    const filtered = dropdownListInfo.items
      .filter(({ name }) => (searchWord ? isIncludeTextWithoutWhitespace(name, searchWord) : true))
      .map((item) => ({
        ...item,
        checked: isSelectedDropdownItem(item),
      }));

    if (searchWord && filtered.length > 1) {
      filtered.sort((a, b) => {
        const aUpper = a.name.toUpperCase().replace(/\s+/g, '');
        const bUpper = b.name.toUpperCase().replace(/\s+/g, '');
        const aStartsWith = aUpper.startsWith(normalizedSearch);
        const bStartsWith = bUpper.startsWith(normalizedSearch);
        if (aStartsWith !== bStartsWith) return aStartsWith ? -1 : 1;
        return a.name.localeCompare(b.name);
      });
    }

    dropdownListInfo.filteredItems =
      dropdownListInfo.isAutoValueItems && filtered.length > MAX_DROPDOWN_DISPLAY_COUNT
        ? filtered.slice(0, MAX_DROPDOWN_DISPLAY_COUNT)
        : filtered;
  };

  const setDropdownListByLastToken = () => {
    const lastTokenStatus = lastToken.value?.status ?? 'EMPTY';
    const lastTokenKeyId = lastToken.value?.key?.id;

    switch (lastTokenStatus) {
      case 'EMPTY':
      case 'VALUE_DONE':
        setKeyDropdownList();
        break;

      case 'KEY_ING':
        setKeyDropdownList();
        break;

      case 'KEY_DONE':
      case 'OPERATOR_ING':
        setOperatorDropdownList(lastTokenKeyId);
        break;

      case 'VALUE_ING':
      case 'OPERATOR_DONE':
        setValueDropdownList(
          lastTokenKeyId,
          () =>
            lastToken.value.operator?.id !== 'like' && lastToken.value.operator?.id !== 'not like',
        );
        break;

      default:
        break;
    }
  };

  const scrollSelectedItemIntoView = () => {
    const selectedItem = dropdownListRef.value?.querySelector('.hovered');
    if (selectedItem) {
      selectedItem.scrollIntoView({ block: 'center', inline: 'nearest', behavior: 'smooth' });
    }
  };

  return {
    isShowDropdown,
    dropdownSearchText,
    dropdownListInfo,
    dropdownListRef,
    filterMainRef,
    hoveredDropdownItemIndex,
    isKeyboardNavigating,
    initHoveredDropdownItemIndex,
    showDropdownList,
    hideDropdownList,
    isSelectedDropdownItem,
    setKeyDropdownList,
    setOperatorDropdownList,
    setValueDropdownList,
    setMultiSelectedValues,
    filterByInput,
    setDropdownListByLastToken,
    scrollSelectedItemIntoView,
  };
}
